import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	type ReactNode,
} from "react";
import { authApi, authClient, type CurrentUser } from "@/api/auth";

interface SessionContextValue {
	user: CurrentUser | null;
	isLoading: boolean;
	isAuthed: boolean;
	isAdmin: boolean;
	refresh: () => Promise<void>;
	logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<CurrentUser | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const refresh = useCallback(async () => {
		try {
			const { user } = await authApi.me();
			setUser(user);
		} catch {
			setUser(null);
		} finally {
			setIsLoading(false);
		}
	}, []);

	const logout = useCallback(async () => {
		try {
			await authClient.signOut();
		} finally {
			setUser(null);
		}
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	return (
		<SessionContext.Provider
			value={{
				user,
				isLoading,
				isAuthed: user !== null,
				isAdmin: user?.isAdmin ?? false,
				refresh,
				logout,
			}}
		>
			{children}
		</SessionContext.Provider>
	);
}

export function useSession(): SessionContextValue {
	const ctx = useContext(SessionContext);
	if (!ctx) throw new Error("useSession must be used within SessionProvider");
	return ctx;
}
